/**
 * Placement Validation (QC Dry-Run)
 *
 * Runtime distillation of the check logic exercised by qc-validation.test.ts:
 * a pure function that validates a design placement against a print area
 * without creating anything. Used by the MCP `validate_placement` dry-run
 * tool and by the job processor before design creation, so both paths share
 * one QC implementation.
 *
 * Fabric.js-independent, no I/O.
 */

import { calculatePlacement } from './calculator.js';
import { mm, px } from './types.js';
import type {
  PlacementContext,
  PlacementPreset,
  PlacementResult,
} from './types.js';
import type { RelativeDesignState } from '../design/placementTypes.js';
import { fitInside, placementFromRelative } from '../design/uniformRescale.js';
import { OVERFLOW_TOLERANCE_REL } from './tolerance.js';
import { MM_PER_INCH, PRINT_DPI, pxFromMm } from '../print/dpi.js';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Pixel dimensions of the (trimmed) artwork raster */
export interface ArtworkDimensionsPx {
  widthPx: number;
  heightPx: number;
}

/**
 * Placement specification — the two entry modes supported by the MCP tools:
 *
 * - `relative`: a persisted/derived RelativeDesignState plus the artwork's
 *   pixel dimensions (the trimmed asset dims — designRatio must match them,
 *   see `design_ratio_mismatch`).
 * - `referenceMm`: exact reference measurements in physical millimeters
 *   (design width, horizontal offset of the design center from the
 *   print-area center, distance from the print-area top edge to the design
 *   top edge) — same convention as `ExternalApiDimensions`.
 */
export type PlacementSpec =
  | {
      mode: 'relative';
      state: RelativeDesignState;
      artwork: ArtworkDimensionsPx;
    }
  | {
      mode: 'referenceMm';
      /** Target physical design width in mm */
      widthMm: number;
      /** Horizontal offset of design center from print-area center in mm (0 = centered, negative = left) */
      offsetXCenterMm: number;
      /** Distance from print-area top edge to design top edge in mm */
      offsetYTopMm: number;
      artwork: ArtworkDimensionsPx;
    };

/**
 * Print area context for validation: the placement context (area pixels
 * and `pxPerMm`). Print file pixels are never read from the area; they
 * follow from the physical size by `pxFromMm` (R15, R16).
 */
export type PrintAreaForValidation = PlacementContext;

/**
 * Minimal decoration-method shape for the DPI check.
 * Matches `DecorationMethod.minDpi` and `PositionDecorationMethod.minDpi`.
 */
export interface DecorationMethodForValidation {
  minDpi?: number | null;
}

export type PlacementFindingSeverity = 'error' | 'warning';

export type PlacementFindingCode =
  | 'invalid_input'
  | 'design_exceeds_print_area'
  | 'position_out_of_bounds'
  | 'low_dpi'
  | 'design_ratio_mismatch';

/** Structured QC finding. Errors fail validation; warnings do not. */
export interface PlacementFinding {
  code: PlacementFindingCode;
  severity: PlacementFindingSeverity;
  message: string;
  details?: Record<string, number | string | boolean>;
}

export interface PlacementValidationResult {
  /** True when no error-severity findings exist (warnings allowed) */
  ok: boolean;
  findings: PlacementFinding[];
  /**
   * Raw computed geometry in PrintArea-space (center-based, unclamped).
   * Absent when the inputs were too degenerate to compute geometry.
   */
  placement?: PlacementResult;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fallback minimum print resolution when the decoration method defines none.
 * 150 dpi is the common lower bound for acceptable DTG/print quality.
 */
export const DEFAULT_MIN_DPI = 150;

/** Relative tolerance for designRatio vs. artwork aspect-ratio comparison */
const RATIO_TOLERANCE = 0.01;

// ═══════════════════════════════════════════════════════════════════════════
// INPUT GUARDS
// ═══════════════════════════════════════════════════════════════════════════

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function isFiniteNumber(value: number): boolean {
  return Number.isFinite(value);
}

/**
 * Collect all degenerate/invalid numeric inputs.
 * Returns field names so callers can report exactly what is wrong.
 */
function collectInvalidFields(
  spec: PlacementSpec,
  printArea: PrintAreaForValidation
): string[] {
  const invalid: string[] = [];

  if (!isPositiveFinite(printArea.widthPx)) invalid.push('printArea.widthPx');
  if (!isPositiveFinite(printArea.heightPx)) invalid.push('printArea.heightPx');
  if (!isPositiveFinite(printArea.pxPerMm)) invalid.push('printArea.pxPerMm');

  if (!isPositiveFinite(spec.artwork.widthPx)) {
    invalid.push('artwork.widthPx');
  }
  if (!isPositiveFinite(spec.artwork.heightPx)) {
    invalid.push('artwork.heightPx');
  }

  if (spec.mode === 'relative') {
    const { state } = spec;
    if (!isFiniteNumber(state.relCenterX)) invalid.push('state.relCenterX');
    if (!isFiniteNumber(state.relCenterY)) invalid.push('state.relCenterY');
    if (!isPositiveFinite(state.relWidth)) invalid.push('state.relWidth');
    if (!isPositiveFinite(state.designRatio)) invalid.push('state.designRatio');
  } else {
    if (!isPositiveFinite(spec.widthMm)) invalid.push('widthMm');
    if (!isFiniteNumber(spec.offsetXCenterMm)) invalid.push('offsetXCenterMm');
    if (!isFiniteNumber(spec.offsetYTopMm)) invalid.push('offsetYTopMm');
  }

  return invalid;
}

// ═══════════════════════════════════════════════════════════════════════════
// GEOMETRY (reuses existing conversion helpers — no new placement math)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute raw (unclamped) geometry for a relative-state spec.
 *
 * The scaled height intentionally follows `designRatio` — production
 * printfiles derive the print height from `designRatio`, so validation
 * must judge the same geometry (a mismatching artwork ratio is flagged
 * separately as `design_ratio_mismatch`).
 */
function placementFromRelativeState(
  state: RelativeDesignState,
  artwork: ArtworkDimensionsPx,
  ctx: PlacementContext
): PlacementResult {
  const scaledWidth = state.relWidth * ctx.widthPx;
  return {
    left: state.relCenterX * ctx.widthPx,
    top: state.relCenterY * ctx.heightPx,
    scale: scaledWidth / artwork.widthPx,
    rotationDeg: 0,
    scaledWidth,
    scaledHeight: scaledWidth * state.designRatio,
  };
}

/**
 * Compute raw geometry for reference measurements via `calculatePlacement`.
 * Uses a synthetic preset with `overflow: 'allow'` so nothing is silently
 * clamped — violations must surface as findings, not be corrected away.
 */
function placementFromReferenceMm(
  spec: Extract<PlacementSpec, { mode: 'referenceMm' }>,
  ctx: PlacementContext
): PlacementResult {
  const artworkRatio = spec.artwork.heightPx / spec.artwork.widthPx;
  const targetWidthPx = spec.widthMm * ctx.pxPerMm;
  const targetHeightPx = targetWidthPx * artworkRatio;

  const preset: PlacementPreset = {
    id: 'reference_mm',
    name: 'Reference Measurements',
    description: 'Exact physical reference measurements (MCP placement spec)',
    // Contain box shaped exactly like the artwork → scale honors widthMm
    maxWidth: mm(spec.widthMm),
    maxHeight: px(targetHeightPx),
    positionX: px(ctx.widthPx / 2 + spec.offsetXCenterMm * ctx.pxPerMm),
    // positionY is the design CENTER: top offset plus half the design height
    positionY: px(spec.offsetYTopMm * ctx.pxPerMm + targetHeightPx / 2),
    overflow: 'allow',
  };

  return calculatePlacement(
    preset,
    spec.artwork.widthPx,
    spec.artwork.heightPx,
    ctx
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CHECKS
// ═══════════════════════════════════════════════════════════════════════════

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function checkBounds(
  placement: PlacementResult,
  ctx: PlacementContext,
  findings: PlacementFinding[]
): void {
  const halfW = placement.scaledWidth / 2;
  const halfH = placement.scaledHeight / 2;
  const leftEdge = placement.left - halfW;
  const rightEdge = placement.left + halfW;
  const topEdge = placement.top - halfH;
  const bottomEdge = placement.top + halfH;

  // The same tolerance the carry-over rule uses: a design within it counts
  // as fitting and is clamped inward by every reader, so it is no QC error.
  const tolX = ctx.widthPx * OVERFLOW_TOLERANCE_REL;
  const tolY = ctx.heightPx * OVERFLOW_TOLERANCE_REL;

  const exceedsWidth = placement.scaledWidth > ctx.widthPx + tolX;
  const exceedsHeight = placement.scaledHeight > ctx.heightPx + tolY;

  if (exceedsWidth || exceedsHeight) {
    findings.push({
      code: 'design_exceeds_print_area',
      severity: 'error',
      message: `Design (${round2(placement.scaledWidth)}×${round2(placement.scaledHeight)}px) exceeds print area (${ctx.widthPx}×${ctx.heightPx}px)`,
      details: {
        scaledWidthPx: round2(placement.scaledWidth),
        scaledHeightPx: round2(placement.scaledHeight),
        printAreaWidthPx: ctx.widthPx,
        printAreaHeightPx: ctx.heightPx,
        overflowXPx: round2(Math.max(0, placement.scaledWidth - ctx.widthPx)),
        overflowYPx: round2(Math.max(0, placement.scaledHeight - ctx.heightPx)),
      },
    });
    // Skip the out-of-bounds check: an oversized design can never be in
    // bounds — a second finding for the same root cause is just noise.
    return;
  }

  const outOfBounds =
    leftEdge < -tolX ||
    rightEdge > ctx.widthPx + tolX ||
    topEdge < -tolY ||
    bottomEdge > ctx.heightPx + tolY;

  if (outOfBounds) {
    findings.push({
      code: 'position_out_of_bounds',
      severity: 'error',
      message: `Design position places it outside the print area (edges: L=${round2(leftEdge)}, R=${round2(rightEdge)}, T=${round2(topEdge)}, B=${round2(bottomEdge)})`,
      details: {
        leftEdgePx: round2(leftEdge),
        rightEdgePx: round2(rightEdge),
        topEdgePx: round2(topEdge),
        bottomEdgePx: round2(bottomEdge),
        printAreaWidthPx: ctx.widthPx,
        printAreaHeightPx: ctx.heightPx,
      },
    });
  }
}

/**
 * Effective print resolution of artwork at a physical print width, in dpi,
 * or null when either input cannot be divided. Exported so a frozen print
 * specification records the same number this check warns about, instead of
 * a second implementation drifting from it.
 */
export function effectiveDpi(
  artworkWidthPx: number,
  targetWidthMm: number
): number | null {
  if (!isPositiveFinite(artworkWidthPx) || !isPositiveFinite(targetWidthMm)) {
    return null;
  }
  return artworkWidthPx / (targetWidthMm / MM_PER_INCH);
}

function checkDpi(
  placement: PlacementResult,
  artwork: ArtworkDimensionsPx,
  printArea: PrintAreaForValidation,
  decorationMethod: DecorationMethodForValidation | null | undefined,
  findings: PlacementFinding[]
): void {
  // Physical target width derives from PrintArea-space pixels via pxPerMm
  const targetWidthMm = placement.scaledWidth / printArea.pxPerMm;
  if (!isPositiveFinite(targetWidthMm)) return;

  const dpi = effectiveDpi(artwork.widthPx, targetWidthMm);
  if (dpi === null) return;
  const minDpi = decorationMethod?.minDpi ?? DEFAULT_MIN_DPI;

  if (dpi >= minDpi) return;

  const details: Record<string, number | string | boolean> = {
    effectiveDpi: round2(dpi),
    minDpi,
    targetWidthMm: round2(targetWidthMm),
    artworkWidthPx: artwork.widthPx,
    // Pixel width the artwork needs to reach minDpi at this print size
    requiredWidthPx: Math.ceil((targetWidthMm / MM_PER_INCH) * minDpi),
    // Pixel width of the print file production receives at this design
    // size: the one formula the print data and the freeze use (R16).
    recommendedWidthPx300dpi: pxFromMm(targetWidthMm, PRINT_DPI),
  };

  // Low resolution is a warning, not a hard fail — flagged in the review UI.
  findings.push({
    code: 'low_dpi',
    severity: 'warning',
    message: `Effective print resolution ${round2(dpi)} dpi is below the minimum of ${minDpi} dpi for a ${round2(targetWidthMm / 10)} cm wide print`,
    details,
  });
}

function checkDesignRatio(
  state: RelativeDesignState,
  artwork: ArtworkDimensionsPx,
  findings: PlacementFinding[]
): void {
  const artworkRatio = artwork.heightPx / artwork.widthPx;
  const relativeDiff =
    Math.abs(state.designRatio - artworkRatio) / artworkRatio;

  if (relativeDiff <= RATIO_TOLERANCE) return;

  // designRatio drives production printfile height — a ratio that deviates
  // from the trimmed artwork produces silent production errors.
  findings.push({
    code: 'design_ratio_mismatch',
    severity: 'warning',
    message: `designRatio ${round2(state.designRatio)} does not match the artwork aspect ratio ${round2(artworkRatio)} — printfile height is derived from designRatio`,
    details: {
      designRatio: round2(state.designRatio),
      artworkRatio: round2(artworkRatio),
      artworkWidthPx: artwork.widthPx,
      artworkHeightPx: artwork.heightPx,
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validate a design placement against a print area (QC dry-run).
 *
 * Pure function — no Fabric.js, no I/O. The same function runs in the MCP
 * `validate_placement` dry-run tool and before actual design creation, so
 * identical specs always yield identical QC results.
 *
 * @param spec - Placement spec (RelativeDesignState or reference measurements)
 * @param printArea - Print area context (px dims + pxPerMm)
 * @param decorationMethod - Optional decoration method (minDpi threshold)
 * @returns ok (no errors; warnings allowed), structured findings, raw geometry
 */
export function validatePlacement(
  spec: PlacementSpec,
  printArea: PrintAreaForValidation,
  decorationMethod?: DecorationMethodForValidation | null
): PlacementValidationResult {
  const findings: PlacementFinding[] = [];

  // ── 1. Degenerate inputs → error finding, never an exception ────────────
  const invalidFields = collectInvalidFields(spec, printArea);
  if (invalidFields.length > 0) {
    findings.push({
      code: 'invalid_input',
      severity: 'error',
      message: `Invalid placement input: ${invalidFields.join(', ')} must be finite ${invalidFields.some((f) => f.includes('Center') || f.includes('offset')) ? 'numbers' : 'positive numbers'}`,
      details: { invalidFields: invalidFields.join(', ') },
    });
    return { ok: false, findings };
  }

  // ── 2. Raw geometry via the existing conversion paths ───────────────────
  const placement =
    spec.mode === 'relative'
      ? placementFromRelativeState(spec.state, spec.artwork, printArea)
      : placementFromReferenceMm(spec, printArea);

  // ── 3. Geometric checks (overflow / out of bounds) ──────────────────────
  checkBounds(placement, printArea, findings);

  // Attach what the carry-over rule would store for a fitting design —
  // useful context for the review UI when the raw spec violates the bounds.
  if (spec.mode === 'relative') {
    const overflowFinding = findings.find(
      (f) => f.code === 'design_exceeds_print_area'
    );
    if (overflowFinding?.details) {
      const area = { width: printArea.widthPx, height: printArea.heightPx };
      const contained = placementFromRelative(
        fitInside(spec.state, area),
        area
      );
      overflowFinding.details.containedRelWidth = round2(contained.relWidth);
      overflowFinding.details.containedRelHeight = round2(contained.relHeight);
    }
  }

  // ── 4. Resolution check (warning only) ──────────────────────────────────
  checkDpi(placement, spec.artwork, printArea, decorationMethod, findings);

  // ── 5. designRatio ↔ artwork consistency (relative mode only) ───────────
  if (spec.mode === 'relative') {
    checkDesignRatio(spec.state, spec.artwork, findings);
  }

  return {
    ok: !findings.some((f) => f.severity === 'error'),
    findings,
    placement,
  };
}
